import React from 'react';
import styled from 'styled-components';

import { MagnifyingGlass } from 'src/assets/magnifying-glass';
import { VisuallyHidden } from 'src/components/core/visually-hidden';
import { buttonResetStyles } from 'src/dispensary/core-menu/components/header/header.styles';
import { useSearch } from './use-search';

export const Search = ({
  isKioskPro = false,
  className,
  'data-testid': dataTestId,
}: {
  isKioskPro?: boolean;
  className?: string;
  'data-testid'?: string;
}): JSX.Element | null => {
  const {
    isSearchDisabled,
    isSearchOpen,
    handleOpenSearch,
    handleChange,
    handleBlur,
    searchTerm,
    searchInputRef,
    dispensaryName,
  } = useSearch();

  if (isSearchDisabled) {
    return null;
  }

  return (
    <SearchContainer $isKioskPro={isKioskPro} className={className}>
      {!isKioskPro && (
        <NavButton onClick={handleOpenSearch}>
          <MagnifyingGlass />
          Search
        </NavButton>
      )}

      {(isSearchOpen || isKioskPro) && (
        <SearchForm>
          <VisuallyHidden>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control, jsx-a11y/label-has-for */}
            <label htmlFor='header-search'>Search</label>
          </VisuallyHidden>

          <SearchInputMagnifyingGlass />
          <SearchInput
            type='text'
            id='header-search'
            name='header-search'
            placeholder={`Search ${String(dispensaryName)}`}
            onBlur={handleBlur}
            onChange={handleChange}
            ref={searchInputRef}
            value={searchTerm}
            data-testid={dataTestId}
          />
        </SearchForm>
      )}
    </SearchContainer>
  );
};

const NavButton = styled.button`
  ${buttonResetStyles}

  align-items: center;
  color: currentColor;
  display: block;
  display: flex;
  font-size: var(--dcm-search-btn-font-size);
  font-weight: var(--dcm-search-btn-font-weight);
  gap: var(--dcm-space-2);
  justify-content: space-between;
  line-height: ${20 / 14};
  padding: 8px 0;
`;

const SearchContainer = styled.div<{ $isKioskPro: boolean }>`
  flex: 1;
  position: ${({ $isKioskPro }) => $isKioskPro && 'relative'};

  @media (min-width: 880px) {
    position: relative;
  }
`;

const SearchForm = styled.div`
  left: 20px;
  position: absolute;
  right: 75px;
  top: 50%;
  transform: translateY(-50%);

  @media (min-width: 880px) {
    left: 0;
    right: 0;
  }
`;

const SearchInput = styled.input`
  border: 1px solid var(--dcm-search-input-border-color);
  border-radius: var(--dcm-search-input-border-radius);
  font-family: var(--dcm-search-input-font-family);
  height: var(--dcm-search-input-height);
  padding: 0 0 0 var(--dcm-search-input-padding-left);
  transition: var(--dcm-search-input-transition);
  width: 100%;

  &:focus {
    border-color: var(--dcm-search-input-focus-border-color);
    box-shadow: var(--dcm-search-input-focus-shadow);
    outline: none;
  }
`;

const SearchInputMagnifyingGlass = styled(MagnifyingGlass)`
  color: var(--dcm-search-icon-color);
  left: var(--dcm-search-icon-left);
  pointer-events: none;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
`;
